import {
    Box,
    Checkbox,
    Divider,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Text,
    Tr,
    VStack,
    Wrap,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { TbSearch, TbTrash } from 'react-icons/tb';
import {
    PARTBAY_BLUE,
    PARTBAY_BLUE_HOVER,
    PARTBAY_BLUE_TINT,
    PARTBAY_BLUE_WASH,
    SettingsInlineEdit,
    SettingsOptionRow,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import {
    InlineCellDisplay,
    InlineSelectCell,
} from '../../../../components/settings/UsersInlineCells';
import {
    allowableToDraft,
    draftToAllowable,
    formatPartTypes,
    insuranceScopeLabel,
    isEmptyPartTypes,
    isSamePartTypes,
    isSameRuleInsurance,
    isSameRuleMakes,
    makeScopeLabel,
    normalizeProhibitedInsurance,
    normalizeRuleMakes,
    type DraftPartTypes,
    type RuleInsurance,
    type RuleMakes,
} from '../../../../components/settings/partRuleValues';
import { useSearchInsurance } from '../../../../hooks/useSearchInsurance';
import type { CatalogPositionGroup } from '../../../../hooks/useGetPCDBCatalog';
import type { AllowablePartType } from '../../../../hooks/useGetSettings';

/** Rows shown at once inside a picker panel that lists chosen records. */
const PICKER_PAGE_SIZE = 5;

/** The label a rule carries when it is not narrowed to one position group. */
export const ALL_POSITIONS = 'All Positions';

/* -------------------------------------------------------------------------- */
/* Position                                                                    */
/* -------------------------------------------------------------------------- */

export interface PositionOptions {
    groups: CatalogPositionGroup[];
    allPositionIds: number[];
}

export interface InlinePositionCellProps {
    /** The rule's stored group name; empty or 'All Positions' means unscoped. */
    value: string;
    options?: PositionOptions;
    onSelect: (next: { positionIds: number[]; positionGroupName: string }) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * A part's position, as a single-select of the position groups that part
 * actually has (Front/Rear, Left/Right, …) plus All Positions. The groups are
 * part-specific, so a part the catalog has no groups for reads as plain text
 * rather than offering a dropdown that could only hold one option.
 */
export const InlinePositionCell = ({
    value,
    options,
    onSelect,
    isDisabled = false,
    ariaLabel,
}: InlinePositionCellProps) => {
    const current = value && value.trim() !== '' ? value : ALL_POSITIONS;
    const groups = useMemo(() => options?.groups ?? [], [options]);

    const selectOptions = useMemo(() => {
        const list = [
            { value: ALL_POSITIONS, label: ALL_POSITIONS },
            ...groups.map((group) => ({
                value: group.groupName,
                label: group.displayName || group.groupName,
            })),
        ];
        // A rule can name a group the catalog no longer returns; keep it so the
        // cell still shows what is stored instead of falling back to a dash.
        if (!list.some((option) => option.value === current)) {
            list.push({ value: current, label: current });
        }
        return list;
    }, [groups, current]);

    // With no groups the only possible value is All Positions, so there is
    // nothing to choose between.
    const hasChoice = groups.length > 0;

    return (
        <InlineSelectCell
            value={current}
            options={selectOptions}
            isDisabled={isDisabled || !hasChoice}
            ariaLabel={ariaLabel}
            minW="220px"
            onSelect={(next) => {
                if (next === current) return;
                if (next === ALL_POSITIONS) {
                    onSelect({
                        positionIds: options?.allPositionIds ?? [],
                        positionGroupName: ALL_POSITIONS,
                    });
                    return;
                }
                const group = groups.find((entry) => entry.groupName === next);
                if (!group) return;
                onSelect({ positionIds: group.positionIds, positionGroupName: group.groupName });
            }}
        />
    );
};

/* -------------------------------------------------------------------------- */
/* Part types                                                                  */
/* -------------------------------------------------------------------------- */

export interface PartTypesPickerProps {
    value: DraftPartTypes;
    onChange: (next: DraftPartTypes) => void;
}

/** The aftermarket certifications, in the order they read best. */
const CERTIFICATIONS: Array<{
    key: 'amNonCertified' | 'amCAPA' | 'amKeysIQ';
    label: string;
}> = [
    { key: 'amNonCertified', label: 'Non-Certified' },
    { key: 'amCAPA', label: 'CAPA' },
    { key: 'amKeysIQ', label: 'KeysIQ' },
];

/**
 * A/M carries no certification of its own in the stored shape — an entry with
 * none means all of them — so ticking the parent ticks its children rather than
 * leaving them looking unselected under a selected parent.
 */
const setAftermarket = (value: DraftPartTypes, checked: boolean): DraftPartTypes => ({
    ...value,
    am: checked,
    amNonCertified: checked,
    amCAPA: checked,
    amKeysIQ: checked,
});

/** Aftermarket with no certification left ticked is aftermarket turned off. */
const setCertification = (
    value: DraftPartTypes,
    key: 'amNonCertified' | 'amCAPA' | 'amKeysIQ',
    checked: boolean,
): DraftPartTypes => {
    const next = { ...value, [key]: checked };
    next.am = next.amNonCertified || next.amCAPA || next.amKeysIQ;
    return next;
};

/**
 * One part type as a pill that fills with brand blue when it is on. Six options
 * never needed a checkbox column: as pills they all read at a glance and each is
 * one tap away.
 */
const PartTypePill = ({
    label,
    isSelected,
    onToggle,
}: {
    label: string;
    isSelected: boolean;
    onToggle: () => void;
}) => (
    <Box
        as="button"
        type="button"
        onClick={onToggle}
        aria-pressed={isSelected}
        px={2.5}
        py={1}
        borderRadius="full"
        borderWidth="1px"
        borderColor={isSelected ? PARTBAY_BLUE : 'gray.300'}
        bg={isSelected ? PARTBAY_BLUE : 'white'}
        color={isSelected ? 'white' : 'gray.700'}
        fontSize="xs"
        fontWeight="medium"
        lineHeight="short"
        whiteSpace="nowrap"
        transition="background 0.12s ease, border-color 0.12s ease, color 0.12s ease"
        _hover={
            isSelected
                ? { bg: PARTBAY_BLUE_HOVER, borderColor: PARTBAY_BLUE_HOVER }
                : { bg: PARTBAY_BLUE_TINT, borderColor: PARTBAY_BLUE, color: PARTBAY_BLUE }
        }
    >
        {label}
    </Box>
);

/** The small uppercase caption over a group of pills. */
const PillGroupLabel = ({ children }: { children: string }) => (
    <Text
        fontSize="10px"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
        color="gray.500"
        lineHeight="short"
    >
        {children}
    </Text>
);

/**
 * The allowable part types as a row of pills, with the aftermarket
 * certifications in their own dashed panel that only appears once A/M is on —
 * so the certifications read as a refinement of A/M rather than as three more
 * part types. Ticking nothing at all is what the table shows as "All types",
 * which the hint spells out rather than leaving the row looking unfinished.
 */
export const PartTypesPicker = ({ value, onChange }: PartTypesPickerProps) => (
    <VStack align="stretch" spacing={2}>
        <Wrap spacing={1.5} shouldWrapChildren>
            <PartTypePill
                label="A/M"
                isSelected={value.am}
                onToggle={() => onChange(setAftermarket(value, !value.am))}
            />
            <PartTypePill
                label="Recon / Reman"
                isSelected={value.reconReman}
                onToggle={() => onChange({ ...value, reconReman: !value.reconReman })}
            />
            <PartTypePill
                label="LKQ"
                isSelected={value.lkq}
                onToggle={() => onChange({ ...value, lkq: !value.lkq })}
            />
        </Wrap>

        {value.am && (
            <Box
                px={2}
                py={2}
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.300"
                borderRadius="md"
                bg={PARTBAY_BLUE_WASH}
            >
                <VStack align="stretch" spacing={1.5}>
                    <PillGroupLabel>A/M Certification</PillGroupLabel>
                    <Wrap spacing={1.5} shouldWrapChildren>
                        {CERTIFICATIONS.map((certification) => (
                            <PartTypePill
                                key={certification.key}
                                label={certification.label}
                                isSelected={value[certification.key]}
                                onToggle={() =>
                                    onChange(
                                        setCertification(
                                            value,
                                            certification.key,
                                            !value[certification.key],
                                        ),
                                    )
                                }
                            />
                        ))}
                    </Wrap>
                </VStack>
            </Box>
        )}

        {isEmptyPartTypes(value) && (
            <Text fontSize="xs" color="gray.500">
                Nothing selected covers every part type.
            </Text>
        )}
    </VStack>
);

export interface InlinePartTypesCellProps {
    value: AllowablePartType[];
    onSave: (next: AllowablePartType[]) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * Part types for one rule. Ticking several boxes takes more than one click, so
 * the change is committed when the panel closes.
 */
export const InlinePartTypesCell = ({
    value,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlinePartTypesCellProps) => {
    const current = useMemo(() => allowableToDraft(value ?? []), [value]);
    const [draft, setDraft] = useState<DraftPartTypes>(current);

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatPartTypes(current)}
                    isMuted={!current.am && !current.reconReman && !current.lkq}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            // The pills need a settled width of their own, and a fixed width has
            // to be set before popper positions the panel.
            minW="260px"
            matchTriggerWidth={false}
            width="260px"
            onOpen={() => setDraft(current)}
            onClose={() => {
                if (!isSamePartTypes(draft, current)) onSave(draftToAllowable(draft));
            }}
        >
            <PartTypesPicker value={draft} onChange={setDraft} />
        </SettingsInlineEdit>
    );
};

/* -------------------------------------------------------------------------- */
/* OEMs                                                                        */
/* -------------------------------------------------------------------------- */

export interface RuleMakesPickerProps {
    value: RuleMakes;
    options: Array<{ _id: string; name: string; }>;
    onChange: (next: RuleMakes) => void;
    maxH?: string;
}

/**
 * All OEMs, or a chosen few. Unlike the makes picker on a user record, `all`
 * is stored as a sentinel rather than as every id, so a rule keeps covering
 * makes added later; ticking every box on the list collapses back to it.
 */
export const RuleMakesPicker = ({
    value,
    options,
    onChange,
    maxH = '200px',
}: RuleMakesPickerProps) => {
    const [search, setSearch] = useState('');

    const selectable = useMemo(
        () =>
            options
                .filter((option) => option.name && option.name.trim() !== '')
                .sort((a, b) => a.name.localeCompare(b.name)),
        [options],
    );

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return selectable;
        return selectable.filter((option) => option.name.toLowerCase().includes(query));
    }, [selectable, search]);

    const isAll = value === 'all';
    const selectedIds = useMemo(
        () => new Set(isAll ? [] : value.map((make) => make._id)),
        [isAll, value],
    );

    const toggle = (option: { _id: string; name: string; }, checked: boolean) => {
        // Coming off All means starting from every make, so unticking one is
        // "all except this" rather than a wipe.
        const base = isAll ? selectable : (value as Array<{ _id: string; name: string; }>);
        const next = checked
            ? [...base.filter((make) => make._id !== option._id), { _id: option._id, name: option.name }]
            : base.filter((make) => make._id !== option._id);
        onChange(next.length === selectable.length || next.length === 0 ? 'all' : next);
    };

    return (
        <VStack align="stretch" spacing={2}>
            {selectable.length > 6 && (
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        value={search}
                        placeholder="Search OEMs"
                        onChange={(e) => setSearch(e.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>
            )}
            {/* Checkboxes rather than the tinted option rows the other cell
                dropdowns use: All OEMs marks every make selected, and a whole
                panel of filled rows is unreadable at this length. */}
            <Checkbox
                size="sm"
                colorScheme="partbay-blue"
                isChecked={isAll}
                isIndeterminate={!isAll && selectedIds.size > 0}
                onChange={(e) => onChange(e.target.checked ? 'all' : [])}
            >
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                    All OEMs
                </Text>
            </Checkbox>
            <Divider />
            <VStack align="stretch" spacing={1} maxH={maxH} overflowY="auto">
                {visibleOptions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" px={1} py={1}>
                        No matches
                    </Text>
                ) : (
                    visibleOptions.map((option) => (
                        <Checkbox
                            key={option._id}
                            size="sm"
                            colorScheme="partbay-blue"
                            isChecked={isAll || selectedIds.has(option._id)}
                            onChange={(e) => toggle(option, e.target.checked)}
                        >
                            <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                {option.name}
                            </Text>
                        </Checkbox>
                    ))
                )}
            </VStack>
        </VStack>
    );
};

export interface InlineRuleMakesCellProps {
    value: RuleMakes;
    options: Array<{ _id: string; name: string; }>;
    onSave: (next: RuleMakes) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/** The OEM scope for one rule, saved when the panel closes. */
export const InlineRuleMakesCell = ({
    value,
    options,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlineRuleMakesCellProps) => {
    const current = useMemo(() => normalizeRuleMakes(value, options), [value, options]);
    const [draft, setDraft] = useState<RuleMakes>(current);

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={makeScopeLabel(current)}
                    isMuted={current === 'all'}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="260px"
            onOpen={() => setDraft(current)}
            onClose={() => {
                if (!isSameRuleMakes(draft, current)) onSave(draft);
            }}
        >
            <RuleMakesPicker value={draft} options={options} onChange={setDraft} />
        </SettingsInlineEdit>
    );
};

/* -------------------------------------------------------------------------- */
/* Prohibited insurance                                                        */
/* -------------------------------------------------------------------------- */

export interface RuleInsurancePickerProps {
    value: RuleInsurance;
    onChange: (next: RuleInsurance) => void;
    /** Empty-list copy. Forbidden-part rules treat empty as every carrier. */
    emptyLabel?: string;
}

/**
 * Which carriers a forbidden-part rule applies to: every one of them, or a
 * searched-for list. The insurance directory is open-ended — an org can name a
 * carrier it has no DRP with — so this searches rather than listing options.
 */
export const RuleInsurancePicker = ({
    value,
    onChange,
    emptyLabel = 'Not set — the rule applies to every carrier.',
}: RuleInsurancePickerProps) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const chosen = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const isAll = value === 'all';

    const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(
        chosen,
        PICKER_PAGE_SIZE,
    );

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, isLoading, error } = useSearchInsurance(debouncedSearch);

    const results = useMemo(() => {
        const picked = new Set(chosen.map((entry) => entry._id));
        return (data?.insuranceCompanies ?? []).filter((entry) => !picked.has(entry._id));
    }, [data, chosen]);

    const isSearching = search.trim().length > 2;

    return (
        <VStack align="stretch" spacing={2}>
            <SettingsOptionRow
                label="All Insurance"
                isEmphasized
                isSelected={isAll}
                onToggle={() => onChange(isAll ? undefined : 'all')}
            />
            <Divider />
            <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                    <Icon as={TbSearch as React.ElementType} color="gray.400" />
                </InputLeftElement>
                <Input
                    value={search}
                    placeholder="Search insurance companies"
                    isDisabled={isAll}
                    onChange={(e) => setSearch(e.target.value)}
                    borderRadius="md"
                />
            </InputGroup>

            {!isAll && isSearching && (
                <Box
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    maxH="160px"
                    overflowY="auto"
                >
                    {isLoading ? (
                        <HStack px={3} py={2} spacing={2}>
                            <Spinner size="xs" color="gray.400" />
                            <Text fontSize="sm" color="gray.500">Searching…</Text>
                        </HStack>
                    ) : error ? (
                        <Text fontSize="sm" color="red.500" px={3} py={2}>
                            Could not search insurance
                        </Text>
                    ) : results.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" px={3} py={2}>
                            No insurance companies found
                        </Text>
                    ) : (
                        results.map((entry) => (
                            <SettingsOptionRow
                                key={entry._id}
                                label={entry.name}
                                isSelected={false}
                                onToggle={() => {
                                    onChange([...chosen, { _id: entry._id, name: entry.name }]);
                                    setSearch('');
                                    setDebouncedSearch('');
                                }}
                            />
                        ))
                    )}
                </Box>
            )}

            {!isAll &&
                (chosen.length === 0 ? (
                    <Text fontSize="xs" color="gray.500">
                        {emptyLabel}
                    </Text>
                ) : (
                    <SettingsSubTable
                        head={
                            <>
                                <SettingsTh>INSURANCE</SettingsTh>
                                <SettingsTh w="44px" aria-label="Remove" />
                            </>
                        }
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    >
                        {pageItems.map((entry) => (
                            <Tr key={entry._id}>
                                <SettingsTd>
                                    <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                        {entry.name}
                                    </Text>
                                </SettingsTd>
                                <SettingsTd textAlign="right">
                                    <IconButton
                                        aria-label={`Remove ${entry.name}`}
                                        icon={<Icon as={TbTrash as React.ElementType} boxSize={4} />}
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => {
                                            const next = chosen.filter(
                                                (current) => current._id !== entry._id,
                                            );
                                            onChange(next.length === 0 ? undefined : next);
                                        }}
                                    />
                                </SettingsTd>
                            </Tr>
                        ))}
                    </SettingsSubTable>
                ))}
        </VStack>
    );
};

export interface InlineRuleInsuranceCellProps {
    value: RuleInsurance;
    onSave: (next: RuleInsurance) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/** Prohibited insurance for one forbidden-part rule, saved when it closes. */
export const InlineRuleInsuranceCell = ({
    value,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlineRuleInsuranceCellProps) => {
    const current = useMemo(() => normalizeProhibitedInsurance(value), [value]);
    const [draft, setDraft] = useState<RuleInsurance>(current);

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={insuranceScopeLabel(current)}
                    isMuted={current === undefined}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="280px"
            // Near the table's right edge, so the panel grows back over the
            // table rather than spilling past it.
            placement="bottom-end"
            matchTriggerWidth={false}
            width="300px"
            onOpen={() => setDraft(current)}
            onClose={() => {
                if (!isSameRuleInsurance(draft, current)) onSave(draft);
            }}
        >
            <RuleInsurancePicker value={draft} onChange={setDraft} />
        </SettingsInlineEdit>
    );
};
